import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { AuthHero, KlysticLogo, StepDot } from "@/components/KlysticBrand";

function formatErr(err) {
  if (err && !err.response) {
    return `Network error — cannot reach ${API}. Check that the backend URL is correct and DNS is live.`;
  }
  const detail = err?.response?.data?.detail;
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function SignupPage() {
  const nav = useNavigate();
  const { finishSignup } = useAuth();

  const [step, setStep] = useState(1); // 1 = credentials, 2 = scan + confirm
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [enrollment, setEnrollment] = useState(null); // { qr_png_base64, provisioning_uri, secret_hint }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startEnrollment = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const data = await api.register({ email, password, name });
      setEnrollment(data);
      setStep(2);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrollment = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      await finishSignup({ email, password, code });
      toast.success("Account activated");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-background">
      <AuthHero
        overline="Create account"
        title="Enroll your"
        highlight="authenticator app."
        description={
          <>
            You&apos;ll set a password, then scan a QR code with Google Authenticator or
            Microsoft Authenticator. From then on, every sign-in requires the 6-digit
            code from your phone.
          </>
        }
      >
        <ol className="space-y-3">
          <StepDot n={1} active={step === 1} done={step > 1}>Set email &amp; password</StepDot>
          <StepDot n={2} active={step === 2} done={false}>Scan QR &amp; enter code</StepDot>
        </ol>
      </AuthHero>

      <div className="lg:col-span-3 flex items-center justify-center p-8">
        {step === 1 ? (
          <form
            onSubmit={startEnrollment}
            data-testid="signup-step1-form"
            className="w-full max-w-md bg-white border border-slate-200 p-8"
          >
            <KlysticLogo className="lg:hidden mb-6" label="UAT · Tracker" />
            <div className="overline text-klystic-sky">Step 1 of 2</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-klystic-ink mt-1">
              Create your account
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Anyone can register. In the next step you&apos;ll enroll your authenticator app.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <Label className="overline text-slate-500">Display name (optional)</Label>
                <Input
                  data-testid="signup-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
                />
              </div>
              <div>
                <Label className="overline text-slate-500">Email</Label>
                <Input
                  data-testid="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
                />
              </div>
              <div>
                <Label className="overline text-slate-500">Password (8+ chars)</Label>
                <Input
                  data-testid="signup-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="signup-next-btn"
              className="w-full mt-6 bg-klystic-ink hover:bg-klystic-ink-dark text-white rounded-none h-11"
            >
              Continue
              <ArrowRight size={16} className="ml-2" />
            </Button>

            <div className="mt-6 pt-6 border-t border-slate-100 text-sm text-slate-500 text-center">
              Already have an account?{" "}
              <Link
                to="/login"
                data-testid="signup-to-login"
                className="text-klystic-sky font-medium hover:text-klystic-sky-dark"
              >
                Sign in
              </Link>
            </div>
          </form>
        ) : (
          <form
            onSubmit={confirmEnrollment}
            data-testid="signup-step2-form"
            className="w-full max-w-md bg-white border border-slate-200 p-8"
          >
            <button
              type="button"
              onClick={() => setStep(1)}
              className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4"
            >
              <ArrowLeft size={12} /> Back
            </button>
            <div className="overline text-klystic-sky">Step 2 of 2</div>
            <h2 className="font-heading text-2xl font-bold tracking-tight text-klystic-ink mt-1">
              Scan the QR code
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Open <strong>Google Authenticator</strong> or{" "}
              <strong>Microsoft Authenticator</strong>, tap add / +, then scan this code.
            </p>

            <div className="mt-6 flex flex-col items-center bg-slate-50 border border-slate-200 p-6">
              <img
                data-testid="signup-qr"
                src={`data:image/png;base64,${enrollment.qr_png_base64}`}
                alt="Scan with Google/Microsoft Authenticator"
                className="w-48 h-48"
              />
              <div className="mt-3 text-xs text-slate-500 font-mono">
                Secret: <span data-testid="signup-secret-hint">{enrollment.secret_hint}</span>
              </div>
            </div>

            <div className="mt-6">
              <Label className="overline text-slate-500 flex items-center gap-2">
                <Smartphone size={12} /> Enter the 6-digit code
              </Label>
              <Input
                data-testid="signup-code"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="rounded-none border-slate-300 font-mono text-center text-2xl tracking-[0.5em] focus-visible:ring-1 focus-visible:ring-klystic-sky focus-visible:border-klystic-sky"
              />
            </div>

            <Button
              type="submit"
              disabled={busy || code.length !== 6}
              data-testid="signup-verify-btn"
              className="w-full mt-6 bg-klystic-ink hover:bg-klystic-ink-dark text-white rounded-none h-11"
            >
              <ShieldCheck size={16} className="mr-2" />
              {busy ? "Verifying…" : "Verify & activate"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
